import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './FadvActivity.css';

const EMAIL_TYPE_LABELS = {
  screening_notification: 'Screening',
  additional_info: 'Additional Info',
  gcic_needed: 'GCIC Needed',
  app_completed: 'App Completed',
  bgv_completed: 'BGV Completed',
  order_cancelled: 'Order Cancelled',
  unknown: 'Unknown',
};

const EMAIL_TYPE_BADGE_CLASS = {
  screening_notification: 'fadv-badge--screening',
  additional_info: 'fadv-badge--additional_info',
  gcic_needed: 'fadv-badge--gcic_needed',
  app_completed: 'fadv-badge--app_completed',
  bgv_completed: 'fadv-badge--bgv_completed',
  order_cancelled: 'fadv-badge--order_cancelled',
  unknown: 'fadv-badge--unknown',
};

function getRatingClass(r) {
  if (!r) return null;
  if (r >= 8) return 'rating-badge--high';
  if (r >= 5) return 'rating-badge--mid';
  return 'rating-badge--low';
}

function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function FadvActivity({ user, onLogout }) {
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const token = localStorage.getItem('token');

  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [recruiters, setRecruiters] = useState([]);

  // Filters
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedRecruiter, setSelectedRecruiter] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [emailType, setEmailType] = useState('all');
  const [matched, setMatched] = useState('all');
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [showRatingDropdown, setShowRatingDropdown] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const ratingDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ratingDropdownRef.current && !ratingDropdownRef.current.contains(e.target)) {
        setShowRatingDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchRecruiters();
  }, []);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompany) params.append('companyId', selectedCompany);
      if (selectedRecruiter) params.append('recruiterId', selectedRecruiter);
      if (dateFilter !== 'all') {
        params.append('dateFilter', dateFilter);
        if (dateFilter === 'custom' && startDate && endDate) {
          params.append('startDate', startDate);
          params.append('endDate', endDate);
        }
      }
      if (emailType !== 'all') params.append('emailType', emailType);
      if (matched !== 'all') params.append('matched', matched);
      if (selectedRatings.length > 0) params.append('ratings', selectedRatings.join(','));

      const resp = await axios.get(`${API_URL}/api/fadv/emails?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmails(resp.data);
    } catch (err) {
      console.error('Error fetching FADV emails:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, selectedRecruiter, dateFilter, startDate, endDate, emailType, matched, selectedRatings, token]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  async function fetchCompanies() {
    try {
      const resp = await axios.get(`${API_URL}/api/companies`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(resp.data || []);
    } catch {}
  }

  async function fetchRecruiters() {
    try {
      const resp = await axios.get(`${API_URL}/api/users/recruiters`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecruiters(resp.data || []);
    } catch {}
  }

  async function handleProcessNow() {
    setProcessing(true);
    setProcessResult(null);
    try {
      const resp = await axios.post(`${API_URL}/api/fadv/process`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProcessResult(resp.data);
      fetchEmails();
    } catch (err) {
      setProcessResult({ error: err.response?.data?.error || err.message });
    } finally {
      setProcessing(false);
    }
  }

  async function handleReprocess() {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Re-process ${ids.length} selected email${ids.length > 1 ? 's' : ''}? Their log entries will be deleted and re-fetched from Gmail.`)) return;
    setProcessing(true);
    setProcessResult(null);
    try {
      const resp = await axios.post(`${API_URL}/api/fadv/reprocess`, { messageIds: ids }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProcessResult({ ...resp.data, reprocessed: true });
      setSelectedIds(new Set());
      fetchEmails();
    } catch (err) {
      setProcessResult({ error: err.response?.data?.error || err.message });
    } finally {
      setProcessing(false);
    }
  }

  function toggleRating(r) {
    setSelectedRatings(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  }

  function clearAllFilters() {
    setSelectedCompany('');
    setSelectedRecruiter('');
    setDateFilter('all');
    setStartDate('');
    setEndDate('');
    setEmailType('all');
    setMatched('all');
    setSelectedRatings([]);
  }

  const hasActiveFilters = selectedCompany || selectedRecruiter || dateFilter !== 'all' || emailType !== 'all' || matched !== 'all' || selectedRatings.length > 0;

  const matchedCount = emails.filter(e => e.matched_candidate_id).length;
  const updatedCount = emails.filter(e => e.action_taken && e.action_taken !== 'No status update').length;

  return (
    <div className="fadv-activity">
      <SharedNav user={user} onLogout={onLogout} />

      <div className="page-content">
        <div className="page-header">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginLeft: 'auto' }}>
            {processResult && !processResult.error && (
              <span style={{ fontSize: 12, color: '#059669' }}>
                ✓ {processResult.processed} processed · {processResult.matched} matched · {processResult.updated} updated
              </span>
            )}
            {processResult?.error && (
              <span style={{ fontSize: 12, color: '#dc2626' }}>Error: {processResult.error}</span>
            )}
            {isAdmin && (
              <>
                <button className="process-btn" onClick={handleProcessNow} disabled={processing}>
                  {processing ? '⏳ Processing…' : '⚡ Process Now'}
                </button>
                <button
                  className="process-btn process-btn--reprocess"
                  onClick={handleReprocess}
                  disabled={processing || selectedIds.size === 0}
                  title={selectedIds.size === 0 ? 'Select emails to reprocess' : `Reprocess ${selectedIds.size} selected`}
                >
                  {processing ? '⏳ Processing…' : `Reprocess${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
                </button>
              </>
            )}
          </div>
        </div>

        {/* FILTER BAR */}
        <div className="filter-bar">
          <select value={selectedCompany} onChange={e => setSelectedCompany(e.target.value)} className="filter-select">
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select value={selectedRecruiter} onChange={e => setSelectedRecruiter(e.target.value)} className="filter-select">
            <option value="">All Recruiters</option>
            {recruiters.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>

          <select
            value={dateFilter}
            onChange={e => { setDateFilter(e.target.value); if (e.target.value !== 'custom') { setStartDate(''); setEndDate(''); } }}
            className="filter-select"
          >
            <option value="all">All Dates</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="last3Months">Last 3 Months</option>
            <option value="custom">Custom Range</option>
          </select>
          {dateFilter === 'custom' && (
            <>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="filter-date-input" />
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="filter-date-input" />
            </>
          )}

          <select value={emailType} onChange={e => setEmailType(e.target.value)} className="filter-select">
            <option value="all">All Types</option>
            <option value="screening_notification">Screening Notification</option>
            <option value="additional_info">Additional Info</option>
            <option value="gcic_needed">GCIC Needed</option>
            <option value="app_completed">App Completed</option>
            <option value="bgv_completed">BGV Completed</option>
            <option value="order_cancelled">Order Cancelled</option>
            <option value="unknown">Unknown</option>
          </select>

          <select value={matched} onChange={e => setMatched(e.target.value)} className="filter-select">
            <option value="all">All</option>
            <option value="yes">Matched</option>
            <option value="no">Unmatched</option>
          </select>

          <div className="rating-filter-container" ref={ratingDropdownRef}>
            <button
              className={`rating-filter-btn ${selectedRatings.length > 0 ? 'active' : ''}`}
              onClick={() => setShowRatingDropdown(v => !v)}
            >
              {selectedRatings.length > 0 ? `Rating: ${selectedRatings.sort((a,b)=>a-b).join(', ')}` : 'All Ratings'} ▾
            </button>
            {showRatingDropdown && (
              <div className="rating-dropdown">
                {[1,2,3,4,5,6,7,8,9,10].map(r => (
                  <label key={r} className="rating-option">
                    <input type="checkbox" checked={selectedRatings.includes(r)} onChange={() => toggleRating(r)} />
                    <span style={{ color: r >= 8 ? '#059669' : r >= 5 ? '#d97706' : '#dc2626', fontWeight: 600 }}>{r}</span>
                  </label>
                ))}
                {selectedRatings.length > 0 && (
                  <button className="clear-ratings-btn" onClick={() => setSelectedRatings([])}>Clear</button>
                )}
              </div>
            )}
          </div>

          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>✕ Clear Filters</button>
          )}
        </div>

        {/* SUMMARY BAR */}
        <div className="summary-bar">
          <span><strong>{emails.length}</strong> emails logged</span>
          <span><strong>{matchedCount}</strong> matched</span>
          <span><strong>{updatedCount}</strong> status updates</span>
        </div>

        {/* TABLE */}
        <div className="table-container mtn-scroll-x">
          {loading ? (
            <div className="loading-state">Loading…</div>
          ) : emails.length === 0 ? (
            <div className="empty-state">No FADV emails found. Try "Process Now" to pull from inbox.</div>
          ) : (
            <table className="fadv-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={emails.length > 0 && selectedIds.size === emails.length}
                      onChange={e => setSelectedIds(e.target.checked ? new Set(emails.map(em => em.gmail_message_id)) : new Set())}
                    />
                  </th>
                  <th>Date</th>
                  <th>Candidate Name</th>
                  <th>Email Type</th>
                  <th>FADV Company</th>
                  <th>Matched To</th>
                  <th>Recruiter</th>
                  <th>Rating</th>
                  <th>Action Taken</th>
                  <th>Subject</th>
                </tr>
              </thead>
              <tbody>
                {emails.map(e => {
                  const badgeClass = EMAIL_TYPE_BADGE_CLASS[e.email_type] || 'fadv-badge--unknown';
                  const ratingClass = getRatingClass(e.matched_rating);
                  const isChecked = selectedIds.has(e.gmail_message_id);
                  return (
                    <tr key={e.id} style={isChecked ? { background: '#eff6ff' } : {}}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            setSelectedIds(prev => {
                              const next = new Set(prev);
                              isChecked ? next.delete(e.gmail_message_id) : next.add(e.gmail_message_id);
                              return next;
                            });
                          }}
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{formatDate(e.received_at)}</td>
                      <td style={{ fontWeight: 500 }}>{e.candidate_name || <span className="cell-muted">—</span>}</td>
                      <td><span className={`fadv-badge ${badgeClass}`}>{EMAIL_TYPE_LABELS[e.email_type] || e.email_type}</span></td>
                      <td className="cell-muted">{e.fadv_company_name || '—'}</td>
                      <td>
                        {e.matched_candidate_id
                          ? <span style={{ fontWeight: 500 }}>{e.matched_candidate_name}</span>
                          : <span className="unmatched-dash">—</span>
                        }
                      </td>
                      <td className="cell-muted">{e.matched_recruiter_name || '—'}</td>
                      <td>
                        {ratingClass
                          ? <span className={`rating-badge ${ratingClass}`}>{e.matched_rating}</span>
                          : <span className="cell-muted">—</span>
                        }
                      </td>
                      <td style={{ fontSize: 12, color: e.action_taken && e.action_taken !== 'No status update' ? '#059669' : undefined }}>
                        {e.action_taken || '—'}
                      </td>
                      <td className="subject-cell" title={e.subject}>
                        {e.subject ? (e.subject.length > 60 ? e.subject.slice(0, 60) + '…' : e.subject) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
